import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { format, parseISO } from 'date-fns';
import { ru } from 'date-fns/locale';
import { FileText, ArrowLeft, Mail, Check, X, Download, Contact } from 'lucide-react';
import { toast } from 'react-toastify';
import { useConfig } from '../../contexts/ConfigContext';
import { buildVcard, downloadVcard } from '../../utils/vcardExport';
import './GroupApplicationsPage.css';

const GroupApplicationsPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { config } = useConfig();
    const isMag = config?.code === 'mag';
    const [migrating, setMigrating] = useState(false);

    const { data: group, isLoading: groupLoading, refetch: refetchGroup } = useQuery(
        ['group', id],
        async () => {
            const response = await axios.get(`${API_BASE_URL}/groups/${id}`, {
                withCredentials: true
            });
            return response.data;
        }
    );

    const { data: applications, isLoading: appsLoading, refetch } = useQuery(
        ['groupApplications', id],
        async () => {
            const response = await axios.get(`${API_BASE_URL}/applications/group/${id}`, {
                withCredentials: true
            });
            return response.data;
        }
    );

    const updateApplicationStatus = async (appId, status) => {
        try {
            await axios.put(
                `${API_BASE_URL}/applications/${appId}/status`,
                { status },
                { withCredentials: true }
            );

            const statusLabels = {
                approved: 'одобрена',
                rejected: 'отклонена',
                contacted: 'связались',
                pending: 'новая'
            };

            toast.success(`Заявка ${statusLabels[status]}`);
            refetch();
        } catch (error) {
            console.error('Ошибка обновления статуса:', error);
            toast.error(error.response?.data?.message || 'Ошибка обновления статуса');
        }
    };

    const migrateLegacyApplications = async () => {
        if (!group?.legacy_applications_sheet_id || migrating) return;
        setMigrating(true);
        try {
            const response = await axios.post(
                `${API_BASE_URL}/applications/group/${id}/migrate-legacy`,
                {},
                { withCredentials: true }
            );
            const count = response.data?.count ?? 0;
            toast.success(count > 0 ? `Перенесено заявок: ${count}` : 'Заявки перенесены');
            await refetch();
            await refetchGroup();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Ошибка переноса заявок');
        } finally {
            setMigrating(false);
        }
    };


    // Получаем список пользователей с уведомлениями
    const getNotificationRecipients = () => {
        if (!group) return [];
        const recipients = [];

        // Менеджер
        if (group.manager_name) {
            recipients.push(group.manager_name);
        }

        // Пользователи с доступом и уведомлениями
        if (group.access_data && Array.isArray(group.access_data)) {
            group.access_data.forEach(access => {
                if (access.receive_notifications || access.notifications) {
                    recipients.push(access.name);
                }
            });
        }

        return recipients;
    };

    const getStatusBadge = (status) => {
        const statuses = {
            pending: { label: 'Новая', className: 'status-pending' },
            approved: { label: 'Одобрена', className: 'status-approved' },
            rejected: { label: 'Отклонена', className: 'status-rejected' },
            contacted: { label: 'Связались', className: 'status-contacted' }
        };
        return statuses[status] || statuses.pending;
    };

    const downloadContactsVcard = () => {
        if (!applications?.length) return;

        const groupLabel = group?.nomination || group?.name || `Группа ${id}`;
        const vcard = buildVcard(
            applications.map((app) => ({
                name: app.name,
                phone: app.phone,
                note: `Заявка: ${groupLabel}`
            }))
        );

        if (!vcard) {
            toast.error('Нет заявок с корректным номером телефона');
            return;
        }

        const safeName = String(groupLabel)
            .replace(/[^\wа-яА-ЯёЁ\s-]/gi, '')
            .trim()
            .replace(/\s+/g, '-')
            .slice(0, 40) || `group-${id}`;

        downloadVcard(vcard, `контакты-${safeName}.vcf`);
        toast.info(
            'Файл скачан. На телефоне откройте его — контакты предложат импортировать. ' +
            'На iPhone: «Добавить все N контактов», на Android: «Импорт» в «Контакты».',
            { autoClose: 8000 }
        );
    };

    if (groupLoading || appsLoading) {
        return (
            <div className="loading">
                <div className="spinner"></div>
            </div>
        );
    }

    return (
        <div className="group-applications-page">
            <div className="page-header-compact">
                <button className="btn btn-secondary btn-sm" onClick={() => navigate('/')}>
                    <ArrowLeft size={16} />
                    Назад
                </button>
                <div className="header-info">
                    <h2>Заявки: {group?.nomination || 'Группа'}</h2>
                    <p className="applications-total">
                        Всего заявок: <strong>{applications?.length || 0}</strong>
                    </p>
                    {getNotificationRecipients().length > 0 && (
                        <div className="notification-recipients">
                            <Mail size={14} />
                            <span>Уведомления получают: {getNotificationRecipients().join(', ')}</span>
                        </div>
                    )}
                    {group?.legacy_applications_sheet_id && (
                        <button
                            type="button"
                            className="btn btn-primary btn-sm migrate-legacy-btn"
                            onClick={migrateLegacyApplications}
                            disabled={migrating}
                        >
                            <Download size={16} />
                            {migrating ? 'Перенос...' : 'Перенести старые заявки'}
                        </button>
                    )}
                    {applications?.length > 0 && (
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm export-contacts-btn"
                            onClick={downloadContactsVcard}
                            title="Скачать файл .vcf для импорта в телефонную книгу"
                        >
                            <Contact size={16} />
                            Скачать контакты (.vcf)
                        </button>
                    )}
                </div>
            </div>

            {!applications || applications.length === 0 ? (
                <div className="empty-state">
                    <FileText size={48} className="empty-icon" />
                    <p>Заявок пока нет</p>
                </div>
            ) : (
                <div className="applications-table">
                    <table>
                        <thead>
                            <tr>
                                <th className="num-cell">№</th>
                                <th>Дата</th>
                                <th>ФИО</th>
                                <th>Email</th>
                                <th>Телефон</th>
                                <th>Способ связи</th>
                                {isMag && <th>Промокод</th>}
                                <th>Статус</th>
                                <th>Действия</th>
                            </tr>
                        </thead>
                        <tbody>
                            {applications.map((app, index) => (
                                <tr key={app.id}>
                                    <td className="num-cell">{index + 1}</td>
                                    <td className="date-cell">
                                        {format(parseISO(app.created_at), 'dd.MM.yyyy HH:mm', { locale: ru })}
                                    </td>
                                    <td className="name-cell">{app.name}</td>
                                    <td className="email-cell">
                                        <a href={`mailto:${app.email}`}>{app.email}</a>
                                    </td>
                                    <td className="phone-cell">
                                        <a href={`tel:${app.phone}`}>{app.phone}</a>
                                    </td>
                                    <td>{app.preferred_contact || '—'}</td>
                                    {isMag && <td>{app.promo_code || '—'}</td>}
                                    <td className="status-cell">
                                        <span className={`status-badge ${getStatusBadge(app.status).className}`}>
                                            {getStatusBadge(app.status).label}
                                        </span>
                                    </td>
                                    <td className="actions-cell">
                                        <div className="action-buttons">
                                            <button
                                                className="action-btn action-btn-approve"
                                                onClick={() => updateApplicationStatus(app.id, 'approved')}
                                                title="Одобрить заявку"
                                            >
                                                <Check size={14} />
                                            </button>
                                            <button
                                                className="action-btn action-btn-reject"
                                                onClick={() => updateApplicationStatus(app.id, 'rejected')}
                                                title="Отклонить заявку"
                                            >
                                                <X size={14} />
                                            </button>
                                            <button
                                                className="action-btn action-btn-contact"
                                                onClick={() => updateApplicationStatus(app.id, 'contacted')}
                                                title="Отметить как связались"
                                            >
                                                📞
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default GroupApplicationsPage;
